<script setup lang="ts">
import { computed } from 'vue';
import { RouterLink } from 'vue-router';

import LangSwitcher from './LangSwitcher.vue';
import { NAV_GROUPS, NAV_STANDALONE, visibleIn } from './navigation';
import { useLocalePath } from '@/composables/useLocalePath';

const { device = 'desktop' } = defineProps<{
  /** `mobile` renders a flat list; `desktop` renders hover dropdowns. */
  device?: 'desktop' | 'mobile';
}>();

const { locale, localeRoute, isActive } = useLocalePath();

const groups = computed(() =>
  NAV_GROUPS.map((group) => ({
    ...group,
    links: visibleIn(group.links, locale.value),
  })).filter((group) => group.links.length > 0),
);

const standalone = computed(() => visibleIn(NAV_STANDALONE, locale.value));

/** Flattened link list for the mobile layout, which has no dropdowns. */
const flatLinks = computed(() => groups.value.flatMap((group) => group.links));
</script>

<template>
  <div class="nav-container">
    <div class="navigation">
      <template v-if="device === 'mobile'">
        <template v-for="link in flatLinks" :key="link.label">
          <a
            v-if="link.href"
            :href="link.href"
            target="_blank"
            rel="noopener"
            class="nav-item"
            :class="link.modifier"
          >
            {{ $t(link.label) }}
          </a>
          <RouterLink
            v-else-if="link.pageKey"
            :to="localeRoute(link.pageKey)"
            class="nav-item"
            :class="[link.modifier, { active: isActive(link.pageKey) }]"
          >
            {{ $t(link.label) }}
          </RouterLink>
        </template>
      </template>

      <template v-else>
        <div v-for="group in groups" :key="group.label" class="dropdown-container">
          <div class="nav-item" :class="group.modifier">
            <div class="dropdown-arrow-container">
              <div class="dropdown-arrow"></div>
            </div>
            <a class="dropdown-hover-item">{{ $t(group.label) }}</a>
          </div>
          <div class="dropdown-content-container">
            <div class="dropdown-content">
              <ul>
                <li v-for="link in group.links" :key="link.label">
                  <a
                    v-if="link.href"
                    :href="link.href"
                    target="_blank"
                    rel="noopener"
                    class="nav-item"
                    :class="link.modifier"
                  >
                    {{ $t(link.label) }}
                  </a>
                  <RouterLink
                    v-else-if="link.pageKey"
                    :to="localeRoute(link.pageKey)"
                    class="nav-item"
                    :class="[link.modifier, { active: isActive(link.pageKey) }]"
                  >
                    {{ $t(link.label) }}
                  </RouterLink>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </template>

      <RouterLink
        v-for="link in standalone"
        :key="link.label"
        :to="localeRoute(link.pageKey!)"
        class="nav-item"
        :class="[link.modifier, { active: isActive(link.pageKey!) }]"
      >
        {{ $t(link.label) }}
      </RouterLink>
    </div>

    <LangSwitcher />
  </div>
</template>
